import React, { useState, useEffect } from 'react';
import { X, Settings, Calendar, FileText, AlertTriangle, Clock, Hash, Paperclip, MapPin, Save, Plus, Trash2 } from 'lucide-react';
import { getTodayAEST } from '../../utils/dateUtils';
import { supabase } from '../../lib/supabase';

interface PlantMachinery {
  id: string;
  asset_number: string;
  name: string;
  type: string;
  make?: string;
  model?: string;
  year?: number;
  serial_number?: string;
  pin_size?: string;
  between_ears?: string;
  pin_centres?: string;
  status: string;
  service_interval_hours?: number;
  service_interval_months?: number;
  current_hours?: number;
  last_service_date?: string;
  last_service_hours?: number;
  next_service_due_date?: string;
  next_service_due_hours?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface EquipmentAttachment {
  id: string;
  equipment_id: string;
  equipment_type: string;
  name: string;
  location: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface PlantMachineryFormProps {
  plant?: PlantMachinery | null;
  onSubmit: (plantData: Partial<PlantMachinery>) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

export const PlantMachineryForm: React.FC<PlantMachineryFormProps> = ({ 
  plant, 
  onSubmit, 
  onClose, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    asset_number: plant?.asset_number || '',
    name: plant?.name || '',
    type: plant?.type || 'excavator',
    make: plant?.make || '',
    model: plant?.model || '',
    year: plant?.year?.toString() || '',
    serial_number: plant?.serial_number || '',
    pin_size: plant?.pin_size || '',
    between_ears: plant?.between_ears || '',
    pin_centres: plant?.pin_centres || '',
    status: plant?.status || 'active',
    current_location: plant?.current_location || '',
    service_interval_hours: plant?.service_interval_hours?.toString() || '',
    service_interval_months: plant?.service_interval_months?.toString() || '',
    current_hours: plant?.current_hours?.toString() || '0',
    last_service_date: plant?.last_service_date || '',
    last_service_hours: plant?.last_service_hours?.toString() || '',
    notes: plant?.notes || ''
  });

  const [attachments, setAttachments] = useState<EquipmentAttachment[]>([]);
  const [newAttachment, setNewAttachment] = useState({ name: '', location: '', notes: '' });
  const [editingAttachment, setEditingAttachment] = useState<EquipmentAttachment | null>(null);

  useEffect(() => {
    if (plant?.id) {
      fetchAttachments();
    }
  }, [plant?.id]);

  const fetchAttachments = async () => {
    if (!plant?.id) return;

    try {
      const { data, error } = await supabase
        .from('equipment_attachments')
        .select('*')
        .eq('equipment_id', plant.id)
        .eq('equipment_type', 'plant')
        .order('name');

      if (error) throw error;
      setAttachments(data || []);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  const handleAddAttachment = async () => {
    if (!plant?.id || !newAttachment.name.trim()) {
      alert('Please enter an attachment name');
      return;
    }

    try {
      const { error } = await supabase
        .from('equipment_attachments')
        .insert([{
          equipment_id: plant.id,
          equipment_type: 'plant',
          name: newAttachment.name,
          location: newAttachment.location,
          notes: newAttachment.notes
        }]);

      if (error) throw error;

      setNewAttachment({ name: '', location: '', notes: '' });
      fetchAttachments();
    } catch (error) {
      console.error('Error adding attachment:', error);
      alert('Failed to add attachment. Please try again.');
    }
  };

  const handleUpdateAttachment = async (attachment: EquipmentAttachment) => {
    try {
      const { error } = await supabase
        .from('equipment_attachments')
        .update({
          name: attachment.name,
          location: attachment.location,
          notes: attachment.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', attachment.id);

      if (error) throw error;

      setEditingAttachment(null);
      fetchAttachments();
    } catch (error) {
      console.error('Error updating attachment:', error);
      alert('Failed to update attachment. Please try again.');
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (confirm('Are you sure you want to delete this attachment?')) {
      try {
        const { error } = await supabase
          .from('equipment_attachments')
          .delete()
          .eq('id', attachmentId);

        if (error) throw error;
        fetchAttachments();
      } catch (error) {
        console.error('Error deleting attachment:', error);
        alert('Failed to delete attachment. Please try again.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Calculate next service due dates
    let next_service_due_date = null;
    let next_service_due_hours = null;

    if (formData.last_service_date && formData.service_interval_months) {
      const lastServiceDate = new Date(formData.last_service_date);
      const nextDueDate = new Date(lastServiceDate);
      nextDueDate.setMonth(lastServiceDate.getMonth() + Number(formData.service_interval_months));
      next_service_due_date = nextDueDate.toISOString().split('T')[0];
    }

    if (formData.last_service_hours && formData.service_interval_hours) {
      next_service_due_hours = Number(formData.last_service_hours) + Number(formData.service_interval_hours);
    }

    const plantData = {
      asset_number: formData.asset_number.toUpperCase(),
      name: formData.name,
      type: formData.type,
      make: formData.make || null,
      model: formData.model || null,
      year: formData.year ? Number(formData.year) : null,
      serial_number: formData.serial_number || null,
      pin_size: formData.type === 'excavator' ? (formData.pin_size || null) : null,
      between_ears: formData.type === 'excavator' ? (formData.between_ears || null) : null,
      pin_centres: formData.type === 'excavator' ? (formData.pin_centres || null) : null,
      status: formData.status,
      service_interval_hours: formData.service_interval_hours ? Number(formData.service_interval_hours) : null,
      service_interval_months: formData.service_interval_months ? Number(formData.service_interval_months) : null,
      current_hours: formData.current_hours ? Number(formData.current_hours) : 0,
      last_service_date: formData.last_service_date || null,
      last_service_hours: formData.last_service_hours ? Number(formData.last_service_hours) : null,
      next_service_due_date,
      next_service_due_hours,
      notes: formData.notes || null
    };

    await onSubmit(plantData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Check if service is due based on hours
  const isServiceDueByHours = () => {
    if (!formData.current_hours || !formData.service_interval_hours || !formData.last_service_hours) {
      return false;
    }
    const currentHours = Number(formData.current_hours);
    const lastServiceHours = Number(formData.last_service_hours);
    const intervalHours = Number(formData.service_interval_hours);
    return currentHours >= (lastServiceHours + intervalHours);
  };

  // Check if service is due based on date
  const isServiceDueByDate = () => {
    if (!formData.last_service_date || !formData.service_interval_months) {
      return false;
    }
    const lastServiceDate = new Date(formData.last_service_date);
    const nextDueDate = new Date(lastServiceDate);
    nextDueDate.setMonth(lastServiceDate.getMonth() + Number(formData.service_interval_months));
    const today = new Date(getTodayAEST());
    return today >= nextDueDate;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Settings className="h-5 w-5 text-purple-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              {plant ? 'Edit Plant/Machinery' : 'Add Plant/Machinery'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Hash className="inline h-4 w-4 mr-1" />
                Asset Number *
              </label>
              <input
                type="text"
                name="asset_number"
                required
                value={formData.asset_number}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono uppercase"
                placeholder="PLT001"
                style={{ textTransform: 'uppercase' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Equipment Name *
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="20T Excavator"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Equipment Type *
              </label>
              <select
                name="type"
                required
                value={formData.type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="excavator">Excavator</option>
                <option value="skid_steer">Skid Steer</option>
                <option value="loader">Loader</option>
                <option value="dozer">Dozer</option>
                <option value="compactor">Compactor</option>
                <option value="generator">Generator</option>
                <option value="compressor">Compressor</option>
                <option value="pump">Pump</option>
                <option value="crane">Crane</option>
                <option value="forklift">Forklift</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="maintenance">Maintenance</option>
                <option value="sold">Sold</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Make
              </label>
              <input
                type="text"
                name="make"
                value={formData.make}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Caterpillar, Komatsu, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model
              </label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="320D, PC200, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <input
                type="number"
                name="year"
                min="1900"
                max={new Date().getFullYear() + 1}
                value={formData.year}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="2020"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Serial Number
            </label>
            <input
              type="text"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono"
              placeholder="ABC123456789"
            />
          </div>

          {/* Pin Dimensions - Excavators Only */}
          {formData.type === 'excavator' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-1 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-amber-600" />
                Pin Dimensions
              </h4>
              <p className="text-xs text-amber-700 mb-4">
                Used to verify attachment compatibility when purchasing new buckets or attachments
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pin Size
                  </label>
                  <input
                    type="text"
                    name="pin_size"
                    value={formData.pin_size}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="e.g., 65mm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Between the Ears
                  </label>
                  <input
                    type="text"
                    name="between_ears"
                    value={formData.between_ears}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="e.g., 300mm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pin Centres
                  </label>
                  <input
                    type="text"
                    name="pin_centres"
                    value={formData.pin_centres}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="e.g., 400mm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Service Schedule */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-blue-600" />
              Service Schedule
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-medium text-gray-800 mb-3">Time-Based Service</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Interval (months)
                    </label>
                    <input
                      type="number"
                      name="service_interval_months"
                      min="1"
                      max="60"
                      value={formData.service_interval_months}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="6"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Service Date
                    </label>
                    <input
                      type="date"
                      name="last_service_date"
                      value={formData.last_service_date}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-gray-800 mb-3">Hour-Based Service</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Interval (hours)
                    </label>
                    <input
                      type="number"
                      name="service_interval_hours"
                      min="1"
                      value={formData.service_interval_hours}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="250"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Hours
                    </label>
                    <input
                      type="number"
                      name="current_hours"
                      min="0"
                      step="0.1"
                      value={formData.current_hours}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hours at Last Service
                    </label>
                    <input
                      type="number"
                      name="last_service_hours"
                      min="0"
                      step="0.1"
                      value={formData.last_service_hours}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Service Status Indicators */}
            <div className="mt-4 space-y-2">
              {isServiceDueByDate() && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium text-red-800">
                      Service overdue by date schedule
                    </span>
                  </div>
                </div>
              )}
              
              {isServiceDueByHours() && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium text-red-800">
                      Service overdue by hour schedule
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 bg-blue-100 border border-blue-300 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> You can set either time-based (monthly) or hour-based service intervals, or both. 
                The system will track whichever schedule comes due first.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Location
            </label>
            <input
              type="text"
              name="current_location"
              value={formData.current_location}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="e.g., Site A, Workshop, Yard"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Notes
            </label>
            <textarea
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Additional notes about the equipment..."
            />
          </div>

          {/* Attachments Section - Only for Excavators and Skid Steers when Editing */}
          {plant?.id && (formData.type === 'excavator' || formData.type === 'skid_steer') && (
            <div className="border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Paperclip className="inline h-4 w-4 mr-1" />
                Attachments
              </label>

              {/* Add New Attachment */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <p className="text-xs font-medium text-gray-700 mb-2">Add New Attachment</p>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={newAttachment.name}
                    onChange={(e) => setNewAttachment({ ...newAttachment, name: e.target.value })}
                    placeholder="Attachment name (e.g., 12ft Bucket)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <input
                    type="text"
                    value={newAttachment.location}
                    onChange={(e) => setNewAttachment({ ...newAttachment, location: e.target.value })}
                    placeholder="Current location"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <input
                    type="text"
                    value={newAttachment.notes}
                    onChange={(e) => setNewAttachment({ ...newAttachment, notes: e.target.value })}
                    placeholder="Notes"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddAttachment}
                    className="w-full flex items-center justify-center space-x-1 px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Attachment</span>
                  </button>
                </div>
              </div>

              {/* Attachments List */}
              {attachments.length > 0 ? (
                <div className="space-y-2">
                  {attachments.map((attachment) => (
                    <div key={attachment.id} className="bg-white border border-gray-200 rounded-lg p-3">
                      {editingAttachment?.id === attachment.id ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editingAttachment.name}
                            onChange={(e) => setEditingAttachment({ ...editingAttachment, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Attachment name"
                          />
                          <input
                            type="text"
                            value={editingAttachment.location}
                            onChange={(e) => setEditingAttachment({ ...editingAttachment, location: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Location"
                          />
                          <input
                            type="text"
                            value={editingAttachment.notes || ''}
                            onChange={(e) => setEditingAttachment({ ...editingAttachment, notes: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Notes"
                          />
                          <div className="flex space-x-2">
                            <button
                              type="button"
                              onClick={() => handleUpdateAttachment(editingAttachment)}
                              className="flex-1 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                            >
                              <Save className="inline h-3 w-3 mr-1" />
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingAttachment(null)}
                              className="flex-1 px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Paperclip className="h-4 w-4 text-gray-600 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-900">{attachment.name}</span>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                type="button"
                                onClick={() => setEditingAttachment(attachment)}
                                className="text-orange-600 hover:text-orange-800 transition-colors"
                                title="Edit"
                              >
                                <Settings className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteAttachment(attachment.id)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                          {attachment.location && (
                            <div className="flex items-start space-x-1 mb-1">
                              <MapPin className="h-3 w-3 text-gray-500 mt-0.5 flex-shrink-0" />
                              <span className="text-xs text-gray-600">{attachment.location}</span>
                            </div>
                          )}
                          {attachment.notes && (
                            <p className="text-xs text-gray-600 mt-1 pl-4">{attachment.notes}</p>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-3 bg-gray-50 rounded-lg">
                  <Paperclip className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">No attachments added yet</p>
                </div>
              )}
            </div>
          )}

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : (plant ? 'Update Equipment' : 'Add Equipment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};